import { Box, Collapse, Drawer } from '@mui/material';
import PropTypes from 'prop-types';
import React from 'react';
import useResponsive from '../../../hooks/useResponsive';
import ChatPanel from './components/chatPanel';
import ChatResizeHandle from './components/chatResizeHandle';
import { useChatConversation } from './useChatConversation';

ChatDock.propTypes = {
	isOpen: PropTypes.bool,
	width: PropTypes.number.isRequired,
	onClose: PropTypes.func.isRequired,
	onResize: PropTypes.func.isRequired,
	onResizeStart: PropTypes.func.isRequired,
	onResizeEnd: PropTypes.func.isRequired,
};

// Mounted once by DashboardLayout, so the thread survives navigation.
export default function ChatDock({
	isOpen,
	width,
	onClose,
	onResize,
	onResizeStart,
	onResizeEnd,
}) {
	const isDesktop = useResponsive('up', 'lg');
	const { messages, isSending, send, retry, stop, reset } =
		useChatConversation();

	// Held here rather than in the panel, so collapsing the dock or crossing the
	// breakpoint keeps the thread and any answer still in flight.
	const panel = (
		<ChatPanel
			messages={messages}
			isSending={isSending}
			onSend={send}
			onStop={stop}
			onRetry={retry}
			onNewChat={reset}
			onClose={onClose}
		/>
	);

	// No content width left to give up, so the panel covers the page instead.
	if (!isDesktop) {
		return (
			<Drawer
				anchor="right"
				open={isOpen}
				onClose={onClose}
				PaperProps={{ sx: { width: '100%', maxWidth: 480 } }}>
				{panel}
			</Drawer>
		);
	}

	// Animating the width reflows the content beside it; unmountOnExit keeps the
	// closed panel out of the tab order.
	return (
		<Collapse
			in={isOpen}
			orientation="horizontal"
			unmountOnExit
			sx={{ flexShrink: 0, height: '100%' }}>
			<Box sx={{ width: width, height: '100%', display: 'flex' }}>
				<ChatResizeHandle
					width={width}
					onResize={onResize}
					onResizeStart={onResizeStart}
					onResizeEnd={onResizeEnd}
				/>
				<Box sx={{ flex: 1, minWidth: 0 }}>{panel}</Box>
			</Box>
		</Collapse>
	);
}
